import { EasPopup } from '@easyvaas/react-components';
import React from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import classNames from 'classnames';
import { useClosePopup } from '../../hooks/useClosePopup';
import { imagePopupState, resultState } from '../../store/state';
import styles from './index.module.scss';
import {
  abilityItemSelector,
  imageItemSelector,
  materialItemSelector,
  pieceItemSelector
} from '../../store/selector';
import SmallText from 'src/components/SmallText';
import { levels } from '../../constants/levels';
import { instanceOfEvoResult } from '../../utils/checker';

interface Props {
  result: ImageV3API.EvolutionResult;
  image: ImageV3State.Image;
}
const CostPanel: React.FC<Props> = ({ result, image }) => {
  const piece = useRecoilValue(pieceItemSelector(image.piecesType));
  const getText = useRecoilCallback(({ snapshot }) => () => {
    if (!piece) return;
    interface Costs {
      [key: string]: number;
    }
    const costs: Costs = {};
    const {
      cost,
      costPieces,
      toolId,
      blessingNumber,
      truePiecesNumber
    } = result;
    if (costPieces) costs[piece.title] = costPieces;
    if (toolId) {
      //道具使用
      const material = snapshot.getLoadable(materialItemSelector(toolId));
      if (material.state === 'hasValue' && material.contents)
        costs[material.contents.title] = 1;
    }
    if (truePiecesNumber && piece.specialExtend?.polishToolId) {
      //真宝石使用
      const material = snapshot.getLoadable(
        materialItemSelector(piece.specialExtend.polishToolId)
      );
      if (material.state === 'hasValue' && material.contents)
        costs[material.contents.title] = truePiecesNumber;
    }
    if (cost) costs['金币'] = cost;
    if (blessingNumber) costs['祝福碎片'] = blessingNumber;

    return Object.entries(costs)
      .map(([k, v]) => `${k}*${v}`)
      .join('，');
  });
  if (!result || !image) return null;

  return (
    <div className={classNames(styles.cost)}>
      {!result.success && image.level < 3 && (
        <div className={styles.aname}>基础成功率+10%</div>
      )}
      <div>消耗{getText()}</div>
    </div>
  );
};

interface EvoProps {
  result: ImageV3API.EvolutionResult;
}
const EvolutionResultPopup: React.FC<EvoProps> = ({ result }) => {
  const { visible, target } = useRecoilValue(imagePopupState('result'));
  const close = useClosePopup();
  const currImage = useRecoilValue(imageItemSelector(target));
  const nextImage = useRecoilValue(
    imageItemSelector(currImage?.advanceInfo?.imgId || 0)
  );
  const image = result.success ? nextImage : currImage;
  const ability1 = useRecoilValue(abilityItemSelector(image?.talent || 0));
  const ability2 = useRecoilValue(abilityItemSelector(image?.highTalent || 0));
  if (!currImage || !image) return null;
  return (
    <EasPopup
      className={classNames(
        styles.container,
        styles[`type${result.success ? 1 : 0}`]
      )}
      visible={visible}
      onClose={() => close('result')}
      position="center"
    >
      <div className={styles.title}>
        进阶
        {result.success ? '成功' : '失败'}
      </div>
      <button className={styles.close} onClick={() => close('result')} />
      <div className={styles.content}>
        <div className={styles.logo}>
          <img src={image.url} alt="" />
        </div>
        <div className={styles.name}>
          {image.title}
          <SmallText className={styles.level} fontSize={18} inline>
            {levels(image.level)}
          </SmallText>
        </div>
        {ability1 && (
          <div className={styles.ability}>
            普通天赋：{ability1.title}
            <div>{ability1.desc}</div>
          </div>
        )}
        {ability2 && (
          <div className={styles.ability}>
            高级天赋：{ability2.title}
            <div>{ability2.desc}</div>
          </div>
        )}
        <CostPanel result={result} image={currImage} />
      </div>
    </EasPopup>
  );
};

interface PolishProps {
  result: ImageV3API.PolishResult;
}
const PolishResultPopup: React.FC<PolishProps> = ({ result }) => {
  const { visible, target } = useRecoilValue(imagePopupState('result'));
  const close = useClosePopup();
  const material = useRecoilValue(materialItemSelector(target));
  if (!material || !result) return null;
  return (
    <EasPopup
      className={classNames(styles.container, styles.type1)}
      visible={visible}
      onClose={() => close('result')}
      position="center"
    >
      <div className={styles.title}>打磨成功</div>
      <button className={styles.close} onClick={() => close('result')} />
      <div className={styles.content}>
        <div className={styles.logo}>
          <img src={material.logo} alt="" />
          <div className={styles.count}>
            <span>{result.number}</span>
          </div>
        </div>
        <div className={styles.name}>{material.title}</div>
        <div className={styles.tips}>
          你成功打磨获得 {material.title}*{result.number}
        </div>
      </div>
    </EasPopup>
  );
};

const ResultPopup = () => {
  const result = useRecoilValue(resultState);
  console.log(result);
  if (!result) return null;
  if (instanceOfEvoResult(result))
    return <EvolutionResultPopup result={result} />;
  return <PolishResultPopup result={result} />;
};

export default React.memo(ResultPopup);
